import { formatHumanName, MedplumClient } from '@medplum/core';
import {
  DiagnosticReport,
  ServiceRequest,
  Patient,
  Specimen,
  Bundle,
  Extension,
} from '@medplum/fhirtypes';
import { TiptapJSON, SignerEntry } from '../types/template.types';
import {
  ARKON,
  SYSTEMS,
  TEMPLATE_ID_EXTENSION_URL,
  TEMPLATE_VERSION_EXTENSION_URL,
  TIPTAP_CONTENT_EXTENSION_URL,
} from '@consts/systems.consts';
import { TEST_RESULT_CODES } from '@consts/genetic-tests';

export interface SaveReportOptions {
  /** Tiptap JSON content */
  content: TiptapJSON;
  /** Template ID used */
  templateId: string;
  /** Template version */
  templateVersion: string;
  /** Review result */
  reviewResult?: 'positive' | 'negative';
  /** Conclusion text (extracted from content) */
  conclusion?: string;
  /** Methodology text */
  methodologies?: string;
  /** References text */
  references?: string;
  /** Dynamic signer list */
  signers?: SignerEntry[];
  /** Report status */
  status: 'registered' | 'preliminary' | 'final';
}

/**
 * Service for saving and loading report content
 */
export class ReportSaveService {
  constructor(private medplum: MedplumClient) {}

  /**
   * Save report content as a new DiagnosticReport or update existing
   * @param serviceRequest - FHIR ServiceRequest resource
   * @param patient - FHIR Patient resource
   * @param specimens - Array of FHIR Specimen resources
   * @param options - Save options including content and status
   * @param existingReportId - Optional existing report ID for updates
   * @returns Created or updated DiagnosticReport
   */
  async saveReport(
    serviceRequest: ServiceRequest,
    patient: Patient,
    specimens: Specimen[],
    options: SaveReportOptions,
    existingReportId?: string
  ): Promise<DiagnosticReport> {
    const {
      content,
      templateId,
      templateVersion,
      reviewResult,
      conclusion,
      methodologies,
      references,
      signers,
      status,
    } = options;

    // Build extensions
    const extensions: Extension[] = [
      // Tiptap content as base64 encoded JSON
      {
        url: TIPTAP_CONTENT_EXTENSION_URL,
        valueString: JSON.stringify(content),
      },
      // Template metadata
      {
        url: TEMPLATE_ID_EXTENSION_URL,
        valueString: templateId,
      },
      {
        url: TEMPLATE_VERSION_EXTENSION_URL,
        valueString: templateVersion,
      },
    ];

    // Add methodologies if present
    if (methodologies?.trim()) {
      extensions.push({
        url: ARKON.SD.DIAGNOSTIC_REPORT.METHODOLOGIES,
        valueString: methodologies.trim(),
      });
    }

    // Add references if present
    if (references?.trim()) {
      extensions.push({
        url: ARKON.SD.DIAGNOSTIC_REPORT.REFERENCES,
        valueString: references.trim(),
      });
    }

    // Add signer list as JSON
    if (signers && signers.length > 0) {
      extensions.push({
        url: `${TIPTAP_CONTENT_EXTENSION_URL}/signatures`,
        valueString: JSON.stringify(signers),
      });
    }

    // Build conclusion code
    const conclusionCode = reviewResult
      ? [
          {
            coding: [
              {
                system: SYSTEMS.SNOMED,
                code:
                  reviewResult === 'positive'
                    ? TEST_RESULT_CODES.POSITIVE.code
                    : TEST_RESULT_CODES.NEGATIVE.code,
                display:
                  reviewResult === 'positive'
                    ? TEST_RESULT_CODES.POSITIVE.display
                    : TEST_RESULT_CODES.NEGATIVE.display,
              },
            ],
            text:
              reviewResult === 'positive'
                ? TEST_RESULT_CODES.POSITIVE.display
                : TEST_RESULT_CODES.NEGATIVE.display,
          },
        ]
      : undefined;

    if (existingReportId) {
      // Update existing report
      const existing = await this.medplum.readResource(
        'DiagnosticReport',
        existingReportId
      );

      // Filter out our extensions and add new ones
      const filteredExtensions = (existing.extension || []).filter(
        (ext) =>
          !ext.url?.startsWith(TIPTAP_CONTENT_EXTENSION_URL) &&
          ext.url !== TEMPLATE_ID_EXTENSION_URL &&
          ext.url !== TEMPLATE_VERSION_EXTENSION_URL &&
          ext.url !== ARKON.SD.DIAGNOSTIC_REPORT.METHODOLOGIES &&
          ext.url !== ARKON.SD.DIAGNOSTIC_REPORT.REFERENCES
      );

      const updatedReport: DiagnosticReport = {
        ...existing,
        status: existing.status === 'preliminary' ? 'preliminary' : status,
        conclusion: conclusion || existing.conclusion,
        conclusionCode: conclusionCode || existing.conclusionCode,
        extension: [...filteredExtensions, ...extensions],
      };

      return this.medplum.updateResource(updatedReport);
    } else {
      // Create new report
      const diagnosticReport: DiagnosticReport = {
        resourceType: 'DiagnosticReport',
        status,
        code: serviceRequest.code || {
          text: 'Laboratory Report',
        },
        subject: {
          reference: `Patient/${patient.id}`,
          display: formatHumanName(patient.name?.[0]),
        },
        basedOn: [
          {
            reference: `ServiceRequest/${serviceRequest.id}`,
          },
        ],
        specimen: specimens.map((s) => ({
          reference: `Specimen/${s.id}`,
        })),
        effectiveDateTime: new Date().toISOString(),
        conclusion,
        conclusionCode,
        extension: extensions,
      };

      // Create bundle with report and update service request
      const bundle = this.createReportBundle(diagnosticReport, serviceRequest);
      const result = await this.medplum.executeBatch(bundle);

      // Find created report in result
      const createdEntry = result.entry?.find(
        (e) => e.resource?.resourceType === 'DiagnosticReport'
      );

      return createdEntry?.resource as DiagnosticReport;
    }
  }

  /**
   * Load Tiptap content from a DiagnosticReport
   * @param reportId - DiagnosticReport resource ID
   * @returns Loaded content with template info or null
   */
  async loadReportContent(reportId: string): Promise<{
    content: TiptapJSON;
    templateId: string;
    signers?: SignerEntry[];
  } | null> {
    try {
      const report = await this.medplum.readResource(
        'DiagnosticReport',
        reportId
      );

      const contentExt = report.extension?.find(
        (ext) => ext.url === TIPTAP_CONTENT_EXTENSION_URL
      );
      const templateIdExt = report.extension?.find(
        (ext) => ext.url === TEMPLATE_ID_EXTENSION_URL
      );
      const signaturesExt = report.extension?.find(
        (ext) => ext.url === `${TIPTAP_CONTENT_EXTENSION_URL}/signatures`
      );

      if (!contentExt?.valueString) {
        return null;
      }

      return {
        content: JSON.parse(contentExt.valueString),
        templateId: templateIdExt?.valueString || '',
        signers: signaturesExt?.valueString
          ? (JSON.parse(signaturesExt.valueString) as SignerEntry[])
          : undefined,
      };
    } catch (error) {
      console.error('Failed to load report content:', error);
      return null;
    }
  }

  /**
   * Check if a DiagnosticReport has Tiptap content
   * @param report - DiagnosticReport resource to check
   * @returns True if report has Tiptap content extension
   */
  hasTiptapContent(report: DiagnosticReport): boolean {
    return !!report.extension?.some(
      (ext) => ext.url === TIPTAP_CONTENT_EXTENSION_URL
    );
  }

  /**
   * Create bundle for saving report
   * @param report - DiagnosticReport resource to save
   * @param serviceRequest - ServiceRequest to link
   * @returns Bundle transaction
   */
  private createReportBundle(
    report: DiagnosticReport,
    serviceRequest: ServiceRequest
  ): Bundle {
    return {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [
        {
          resource: report,
          request: {
            method: 'POST',
            url: 'DiagnosticReport',
          },
        },
        // Update service request to link to report
        {
          resource: {
            ...serviceRequest,
            // Add or update any necessary references
          },
          request: {
            method: 'PUT',
            url: `ServiceRequest/${serviceRequest.id}`,
          },
        },
      ],
    };
  }
}

/**
 * Create a report save service instance
 * @param medplum - Medplum client instance
 * @returns New ReportSaveService instance
 */
export function createReportSaveService(
  medplum: MedplumClient
): ReportSaveService {
  return new ReportSaveService(medplum);
}
